var _excluded = ["sticky", "size", "rowHeight", "className", "scrollBarWidth", "positionSticky", "children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { forwardRef, useMemo } from 'react';
import { createArray, isNumber } from '@okee-uikit/react-utils';
import { useTableImplement, useTableCep } from '../implements/implement';
import { useTableColumns } from '../col-query/column-info';
import { useTable } from '../context';
import { useTableCaptionsHeight, useTableColumnsWidth } from '../implements/size';
import { useTableSections } from '../implements/sections';
import { TableCell } from './cell';
import { TableRow } from './row';
import { TableFilterTrigger, TableSortTrigger, TableColumnResizeTrigger } from './features';
import { SummaryContext } from '../summary';
export var TableHead = forwardRef(function (props, ref) {
  var _props$sticky = props.sticky,
      sticky = _props$sticky === void 0 ? true : _props$sticky,
      size = props.size,
      headRowHeight = props.rowHeight,
      className = props.className,
      _props$scrollBarWidth = props.scrollBarWidth,
      scrollBarWidth = _props$scrollBarWidth === void 0 ? 0 : _props$scrollBarWidth,
      positionSticky = props.positionSticky,
      children = props.children,
      attrs = _objectWithoutProperties(props, _excluded);

  var cep = useTableCep();
  var implement = useTableImplement();
  var context = useTable();
  var captionsHeight = useTableCaptionsHeight();
  var columnsWidth = useTableColumnsWidth();
  var sections = useTableSections();

  var _useTableColumns = useTableColumns(),
      leafColumns = _useTableColumns.leafColumns;

  var rowBordered = sections.rowBordered;
  var rowHeight = headRowHeight && headRowHeight + (rowBordered && 1 || 0);
  return useMemo(function () {
    var elem = cep.elem;
    var isTable = implement.isTable,
        THead = implement.THead;
    var deep = context.deep,
        colCount = context.colCount,
        columns = context.columns,
        filterState = context.filterState,
        filterActions = context.filterActions,
        sortState = context.sortState,
        sortActions = context.sortActions;
    var theadHeights = captionsHeight.theadHeights;
    var widthMap = columnsWidth.widthMap,
        stickyStartMap = columnsWidth.stickyStartMap,
        stickyEndMap = columnsWidth.stickyEndMap,
        resizeMouseDown = columnsWidth.resizeMouseDown;
    var widthList = leafColumns.map(function (col) {
      return widthMap.get(col);
    });
    var stickyStartList = leafColumns.map(function (col) {
      return stickyStartMap.get(col);
    });
    var stickyEndList = leafColumns.map(function (col) {
      return stickyEndMap.get(col);
    });
    var noneStickyFirstColumns = [];
    {
      var process = function process(cols) {
        var first = cols.find(function (col) {
          return col.show;
        });

        if (first) {
          noneStickyFirstColumns.push(first);
          first.children && process(first.children);
        }
      };

      process(columns.filter(function (col) {
        return !col.fixed;
      }));
    }
    var stickyTops = sticky && theadHeights.length && createArray(deep, function (row) {
      var top = 0;

      for (var i = 0; i < row; i++) {
        top += theadHeights[i];
      }

      return top;
    }) || [];
    var stickySummaryTops = (sticky && theadHeights.length && createArray(theadHeights.length, function (row) {
      var top = 0;

      for (var i = 0; i < row; i++) {
        top += theadHeights[i];
      }

      return top;
    }) || []).slice(deep);
    var rendererGrid = createArray(deep, function () {
      return createArray(colCount, function () {
        return undefined;
      });
    });
    var whitespaceGrid = createArray(deep, function () {
      return createArray(colCount, function () {
        return undefined;
      });
    });

    var createRenderer = function createRenderer(cols) {
      var totalColSpan = 0;
      cols.forEach(function (col) {
        var id = col.id,
            show = col.show,
            level = col.level,
            path = col.path,
            children = col.children,
            title = col.title,
            colIndex = col.colIndex,
            headCellProps = col.headCellProps,
            resizable = col.resizable;
        var key = "L".concat(level, ".").concat(path.join('.'));
        var stickyStartInfo = stickyStartMap.get(col);
        var stickyEndInfo = stickyEndMap.get(col);
        var y = level - 1;
        var x = colIndex - 1;
        var rowIndex = level;
        var rowSpan = !children && rendererGrid.length - level + 1 || 1;
        var colSpan = show && 1 || 0;
        var offset;

        if (!isTable) {
          offset = 0;

          for (var preX = x - 1; preX > -1; preX--) {
            var preWhitespace = whitespaceGrid[y][preX];

            if (preWhitespace === undefined) {
              break;
            }

            offset += preWhitespace;
          }
        }

        var width;

        if (!isTable) {
          width = widthMap.get(col);
          whitespaceGrid[y][x] = show ? undefined : width;

          if (!children) {
            for (var postY = y + 1; postY < deep; postY++) {
              whitespaceGrid[postY][x] = width;
            }
          }
        }

        var height;

        if (!isTable && rowSpan > 1) {
          height = isNumber(height) && rowSpan * height || "".concat(rowSpan * 100, "%");
        }

        var filterTrigger = function filterTrigger() {
          if (filterState && filterActions && col.filterOptions) {
            return React.createElement(TableFilterTrigger, {
              key: "$$filterTrigger",
              id: id,
              columnFilterOptions: col.filterOptions,
              state: filterState,
              actions: filterActions
            });
          }

          return null;
        };

        var sortTrigger = function sortTrigger() {
          if (sortState && sortActions && col.sortOptions) {
            return React.createElement(TableSortTrigger, {
              key: "$$sortTrigger",
              id: id,
              state: sortState,
              actions: sortActions
            });
          }

          return null;
        };

        var columnResizeTrigger = function columnResizeTrigger() {
          if (resizable && colIndex < cols.length) {
            return React.createElement(TableColumnResizeTrigger, {
              key: "$$resizeTrigger",
              id: id,
              onMouseDown: function onMouseDown(e) {
                return resizeMouseDown(e, colIndex);
              }
            });
          }

          return null;
        };

        var currentRender = function currentRender() {
          return React.createElement(TableCell, _objectSpread(_objectSpread({}, headCellProps(context)), {}, {
            key: "columnHeader.".concat(key),
            header: "col",
            stickyTop: stickyTops[y],
            stickyStart: stickyStartInfo === null || stickyStartInfo === void 0 ? void 0 : stickyStartInfo.value,
            stickyEnd: (stickyEndInfo === null || stickyEndInfo === void 0 ? void 0 : stickyEndInfo.value) && (stickyEndInfo === null || stickyEndInfo === void 0 ? void 0 : stickyEndInfo.value) + scrollBarWidth,
            noneStickyFirst: noneStickyFirstColumns.includes(col),
            rowSpan: rowSpan > 1 && rowSpan || undefined,
            colSpan: colSpan > 1 && colSpan || undefined,
            rowIndex: rowIndex,
            colIndex: colIndex,
            offset: offset,
            width: width,
            height: height
          }), React.createElement("span", {
            className: elem('HeadCellContent')
          }, title(context), filterTrigger(), sortTrigger(), columnResizeTrigger()));
        };

        rendererGrid[y][x] = show && currentRender || undefined;

        if (children) {
          colSpan = createRenderer(children);
        }

        totalColSpan += colSpan;
      });
      return totalColSpan;
    };

    createRenderer(columns);
    return React.createElement(THead, _objectSpread({
      ref: ref,
      className: elem('Head', {
        size: size,
        sticky: sticky
      }, className),
      role: "rowgroup"
    }, attrs), rendererGrid.map(function (row, index) {
      return React.createElement(TableRow, {
        key: "row.".concat(index),
        rowHeight: rowHeight
      }, row.map(function (cell) {
        return cell && cell();
      }));
    }), React.createElement(SummaryContext.Provider, {
      value: {
        stickyTops: stickySummaryTops,
        symbolList: [],
        sticky: sticky,
        stickyStartList: stickyStartList,
        stickyEndList: stickyEndList,
        widthList: widthList,
        whitespaceGrid: []
      }
    }, children));
  }, [sticky, size, rowHeight, cep, implement, context, captionsHeight, columnsWidth]);
});